import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  PieChart,
  BarChart3,
  Users,
  Loader2,
  Download,
  FileText,
  AlertCircle,
  Shield,
  Globe,
  Network,
  ChevronDown,
  MessageCircle,
  GitCompareArrows,
  Presentation,
} from 'lucide-react';
import { BehavioralClusterNetwork } from '../components/BehavioralClusterNetwork';
import { toast } from 'sonner';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import * as XLSX from 'xlsx';
import OpenEndedAnalysis from '../components/OpenEndedAnalysis';
import PREAnalytics from '../components/PREAnalytics';
import HRLabReport from '../components/HRLabReport';
import MarketLabViews from '../components/MarketLabViews';
import PharmaLabReport from '../components/PharmaLabReport';
import InterviewDialog from '../components/InterviewDialog';
import ScenarioDialog from '../components/ScenarioDialog';
import ReportHeader from '../components/reporting/ReportHeader';
import { useReportingMode } from '../context/ReportingModeContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuCheckboxItem, DropdownMenuLabel } from '../components/ui/dropdown-menu';

// Extracted analysis components
import {
  SocialDynamicsPanel,
  CascadeAlerts,
  MicroDialogues,
  ExposureAnalysisPanel,
  OverviewTab,
  BreakdownTab,
  AIInsightsTab,
  DataVisualsTab,
} from '../components/analysis';

// Shared utilities
import {
  getScaleColorsMap,
  getOrderedScale,
  getOrderedDemographicValues,
  aggregateNumericValues,
} from '../lib/analysisUtils';


export default function Analysis() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [simulations, setSimulations] = useState([]);
  const [populations, setPopulations] = useState([]);
  const [selectedSimId, setSelectedSimId] = useState(searchParams.get('simulation') || '');
  const [simulation, setSimulation] = useState(null);
  const [stimulus, setStimulus] = useState(null);
  const [population, setPopulation] = useState(null);
  const [aggregation, setAggregation] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [groupBy, setGroupBy] = useState('');
  const [allBreakdowns, setAllBreakdowns] = useState(null);
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [webContextPack, setWebContextPack] = useState(null);
  const [socialDynamics, setSocialDynamics] = useState(null);
  const [socialDialogues, setSocialDialogues] = useState(null);
  const [exposureAnalysis, setExposureAnalysis] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [crossDemographic1, setCrossDemographic1] = useState('');
  const [crossDemographic2, setCrossDemographic2] = useState('');
  const [crossAnalysisData, setCrossAnalysisData] = useState(null);
  const [crossAnalysisLoading, setCrossAnalysisLoading] = useState(false);
  const [availableDemographics, setAvailableDemographics] = useState([]);
  const [exportIncludeReasoning, setExportIncludeReasoning] = useState(false);
  const [exportIncludeConfidence, setExportIncludeConfidence] = useState(false);
  const [fullscreenChart, setFullscreenChart] = useState(null);
  const [copiedData, setCopiedData] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // B3: controlled tabs for overflow menu
  const [interviewOpen, setInterviewOpen] = useState(false); // A1
  const [scenarioOpen, setScenarioOpen] = useState(false); // A3
  const reportRef = useRef(null);
  
  const { isPeopleLab, isMarketLab, isPharmaLab } = useReportingMode();

  // ---- Data fetching ----

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (selectedSimId) { fetchSimulationData(selectedSimId); setSearchParams({ simulation: selectedSimId }); } }, [selectedSimId]);
  useEffect(() => { if (selectedSimId && groupBy) fetchBreakdown(); }, [groupBy, selectedSimId]);
  useEffect(() => { if (selectedSimId) { fetchAggregation(); fetchDemographics(); } }, [filterField, filterValue, selectedSimId]);
  useEffect(() => { if (selectedSimId && population?.columns) fetchAllBreakdowns(); }, [selectedSimId, population]);
  useEffect(() => { fetchCrossAnalysis(); }, [crossDemographic1, crossDemographic2, selectedSimId]);

  const fetchInitialData = async () => {
    try {
      const [simsRes, popsRes] = await Promise.all([api.get(`/simulations`), api.get(`/populations`)]);
      const completedSims = simsRes.data.filter(s => s.status === 'completed' || s.status === 'completed_with_warning');
      setSimulations(completedSims);
      setPopulations(popsRes.data);
      const urlSimId = searchParams.get('simulation');
      if (urlSimId && completedSims.find(s => s.id === urlSimId)) setSelectedSimId(urlSimId);
      else if (completedSims.length > 0 && !selectedSimId) setSelectedSimId(completedSims[0].id);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const fetchSimulationData = async (simId) => {
    try {
      const simRes = await api.get(`/simulations/${simId}`);
      setSimulation(simRes.data);
      const [stimRes, popRes, aggRes] = await Promise.all([
        api.get(`/stimuli/${simRes.data.stimulus_id}`),
        api.get(`/populations/${simRes.data.population_id}`),
        api.get(`/simulations/${simId}/aggregation`)
      ]);
      setStimulus(stimRes.data); setPopulation(popRes.data); setAggregation(aggRes.data);

      // Web context
      if (stimRes.data?.web_context_pack_id) {
        try { const r = await api.get(`/stimuli/${stimRes.data.id}/web-context`, { _silent: true }); setWebContextPack(r.data?.has_web_context ? r.data : null); } catch { setWebContextPack(null); }
      } else { setWebContextPack(null); }
      
      // PRE analytics - not needed in state anymore since tab handles its own data

      // Social dynamics
      if (simRes.data.social_enabled) {
        try { const r = await api.get(`/simulations/${simId}/social-dynamics`, { _silent: true }); setSocialDynamics(r.data); } catch { setSocialDynamics(null); }
        try { const r = await api.get(`/simulations/${simId}/social-dialogues`, { _silent: true }); setSocialDialogues(r.data); } catch { setSocialDialogues(null); }
      } else { setSocialDynamics(null); setSocialDialogues(null); }
      
      // Exposure analysis
      if (simRes.data.web_context_exposure_rate != null) {
        try { const r = await api.get(`/simulations/${simId}/exposure-analysis`, { _silent: true }); setExposureAnalysis(r.data); } catch { setExposureAnalysis(null); }
      } else { setExposureAnalysis(null); }
      
      // Default group by
      if (popRes.data.columns.length > 0) {
        const col = popRes.data.columns.find(c => { const n = c.name.toLowerCase(); return !n.includes('_id') && n !== 'id' && !n.endsWith('id') && (c.unique_count || 0) <= 25; });
        setGroupBy(col?.name || popRes.data.columns[0].name);
      }
    } catch { toast.error('Failed to fetch simulation data'); }
  };

  const fetchAggregation = async () => {
    if (!selectedSimId) return;
    try {
      let url = `/simulations/${selectedSimId}/aggregation`;
      if (filterField && filterValue) url += `?filter_field=${filterField}&filter_value=${encodeURIComponent(filterValue)}`;
      const r = await api.get(url, { _silent: true });
      setAggregation(r.data);
    } catch (e) { console.error('Failed to fetch aggregation:', e); }
  };

  const fetchBreakdown = async () => {
    if (!selectedSimId || !groupBy) return;
    try { const r = await api.get(`/simulations/${selectedSimId}/demographic-breakdown?group_by=${groupBy}`, { _silent: true }); setBreakdown(r.data); }
    catch { toast.error('Failed to fetch breakdown'); }
  };

  const fetchAllBreakdowns = async () => {
    if (!selectedSimId || !population?.columns) return;
    const cols = population.columns.filter(c => { const n = c.name.toLowerCase(); return !n.includes('_id') && n !== 'id' && !n.endsWith('id') && (c.unique_count || 0) <= 25 && (c.unique_count || 0) >= 2; });
    if (cols.length === 0) return;
    try {
      const results = await Promise.all(cols.map(c => api.get(`/simulations/${selectedSimId}/demographic-breakdown?group_by=${c.name}`, { _silent: true }).catch(() => null)));
      const map = {};
      cols.forEach((c, i) => { if (results[i]?.data?.breakdown) map[c.name] = results[i].data; });
      setAllBreakdowns(map);
    } catch (err) { console.debug('fetchAllBreakdowns failed (silent, optional data):', err); }
  };

  const fetchCrossAnalysis = async () => {
    if (!selectedSimId || !crossDemographic1 || !crossDemographic2) { setCrossAnalysisData(null); setCrossAnalysisLoading(false); return; }
    setCrossAnalysisLoading(true);
    try { const r = await api.get(`/simulations/${selectedSimId}/demographics?group_by=${crossDemographic1}&cross_with=${crossDemographic2}`, { _silent: true }); setCrossAnalysisData(r.data); }
    catch { toast.error('Failed to fetch cross-demographics analysis'); }
    finally { setCrossAnalysisLoading(false); }
  };

  const fetchDemographics = async () => {
    if (!selectedSimId) return;
    try { const r = await api.get(`/simulations/${selectedSimId}/demographics?group_by=all`, { _silent: true }); setAvailableDemographics(r.data.demographics || []); }
    catch (e) { console.error('Failed to fetch demographics:', e); }
  };

  // ---- Export utilities ----

  const exportToExcel = () => {
    if (!aggregation || !simulation || stimulus?.stimulus_type === 'open_text' || !aggregation.distribution) return;
    try {
      const ws_data = [['Simulation Report'], ['Simulation:', simulation.name], ['Population:', population?.name], ['Total Responses:', aggregation.total_responses], [],
        ['Response', 'Count', 'Percentage'],
        ...Object.entries(aggregation.distribution).map(([r, c]) => [r, c, `${aggregation.percentages[r].toFixed(1)}%`])];
      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Results');
      XLSX.writeFile(wb, `${simulation.name.replace(/\s+/g, '_')}_results.xlsx`);
      toast.success('Excel report exported');
    } catch { toast.error('Failed to export Excel'); }
  };

  const exportToCSV = () => {
    if (!aggregation || !simulation || stimulus?.stimulus_type === 'open_text' || !aggregation.distribution) return;
    try {
      let csv = 'Response,Count,Percentage\n';
      Object.entries(aggregation.distribution).forEach(([r, c]) => { csv += `"${r}",${c},${(aggregation.percentages?.[r]?.toFixed(1) || '0')}%\n`; });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `${simulation.name.replace(/\s+/g, '_')}_results.csv`; link.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Failed to export CSV'); }
  };

  const copyChartData = async () => {
    if (!aggregation?.distribution) return;
    try {
      let text = `Results for: ${simulation?.name}\nTotal Responses: ${aggregation.total_responses}\n\nResponse | Count | Percentage\n---------|-------|------------\n`;
      Object.entries(aggregation.distribution).forEach(([r, c]) => { text += `${r} | ${c} | ${(aggregation.percentages?.[r]?.toFixed(1) || '0')}%\n`; });
      await navigator.clipboard.writeText(text);
      setCopiedData(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedData(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const exportDetailedResults = async (format = 'xlsx') => {
    if (!simulation) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ format, include_reasoning: exportIncludeReasoning, include_confidence: exportIncludeConfidence });
      const response = await api.get(`/simulations/${selectedSimId}/export?${params}`, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `${simulation.name.replace(/\s+/g, '_')}_detailed.${format}`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Detailed results exported as ${format.toUpperCase()}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to export detailed results'); }
    finally { setExporting(false); }
  };

  const exportToPDF = async () => {
    // B4: server-side branded PDF (replaces the old html2canvas → jsPDF screenshot).
    if (!simulation?.id) return;
    setExportingPdf(true);
    try {
      const res = await api.get(
        `/simulations/${simulation.id}/report.pdf?include_insights=true`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(simulation.name || 'report').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF report exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // ---- Derived data ----

  const orderedResponseScale = stimulus?.response_scale ? getOrderedScale(stimulus.response_scale) : [];
  const scaleColorMap = stimulus?.response_scale ? getScaleColorsMap(orderedResponseScale, stimulus?.scale_type) : {};
  const scaleColors = orderedResponseScale.map(r => scaleColorMap[r] || '#94A3B8');
  const isOpenEnded = stimulus?.stimulus_type === 'open_text';

  const pieData = !isOpenEnded && aggregation && orderedResponseScale.length > 0
    ? orderedResponseScale.map(r => ({ name: r, value: aggregation.distribution?.[r] || 0 })).filter(i => i.value > 0)
    : !isOpenEnded && aggregation?.distribution
      ? Object.entries(aggregation.distribution).map(([name, value]) => ({ name, value }))
      : [];

  const barData = !isOpenEnded && breakdown ? (() => {
    const agg = aggregateNumericValues(breakdown.breakdown, groupBy);
    const entries = Object.entries(agg || {}).map(([group, data]) => {
      const entry = { name: group };
      Object.entries(data.percentages || {}).forEach(([r, pct]) => { entry[r] = pct; });
      return entry;
    });
    const ordered = getOrderedDemographicValues(entries.map(e => e.name), groupBy);
    return ordered.map(g => entries.find(e => e.name === g)).filter(Boolean);
  })() : [];

  // ---- Render ----

  if (loading) {
    return (
      <div data-testid="analysis-page" className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="h-96 skeleton rounded-md" />
      </div>
    );
  }

  if (simulations.length === 0) {
    return (
      <div data-testid="analysis-page" className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Analysis</h1>
        <Card className="border border-slate-200 rounded-xl">
          <CardContent className="empty-state py-16">
            <PieChart className="empty-state-icon" strokeWidth={1} />
            <h3 className="text-lg font-medium text-slate-900 mt-2">No completed simulations</h3>
            <p className="text-slate-500 text-sm mt-1">Run a simulation to analyze results</p>
            <Button asChild className="mt-6 bg-primary hover:bg-primary/90">
              <Link to="/simulations">Go to Simulations</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="analysis-page" className="space-y-5 page-transition">
      {/* A1: Interview dialog - mounted once at top; opens via header button */}
      <InterviewDialog
        open={interviewOpen}
        onOpenChange={setInterviewOpen}
        simulation={simulation}
        stimulus={stimulus}
      />
      {/* A3: Scenario sequencing dialog */}
      <ScenarioDialog
        open={scenarioOpen}
        onOpenChange={setScenarioOpen}
        simulation={simulation}
      />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analysis</h1>
          <p className="text-sm text-slate-500 mt-0.5">Explore simulation results with demographic filtering</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedSimId} onValueChange={setSelectedSimId}>
            <SelectTrigger className="w-56 h-9 text-sm" data-testid="simulation-selector">
              <SelectValue placeholder="Select simulation" />
            </SelectTrigger>
            <SelectContent>
              {simulations.map(sim => <SelectItem key={sim.id} value={sim.id}>{sim.name}</SelectItem>)}
            </SelectContent>
          </Select>
          
          {simulation && (
            <div className="flex items-center gap-1.5">
              <Button
                onClick={() => setInterviewOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 h-9 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                data-testid="open-interview-dialog-btn"
              >
                <MessageCircle className="w-4 h-4" />Interview
              </Button>
              <Button
                onClick={() => setScenarioOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 h-9 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                data-testid="open-scenario-dialog-btn"
              >
                <GitCompareArrows className="w-4 h-4" />Scenario
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1.5 h-9 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    disabled={exporting || exportingPdf}
                    data-testid="export-menu-btn"
                  >
                    {(exporting || exportingPdf) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Export
                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="export-menu-content">
                  {isPeopleLab && (
                    <>
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Reports</DropdownMenuLabel>
                      <DropdownMenuItem asChild data-testid="export-executive-view-item">
                        <Link to={`/analysis/executive?simulation=${selectedSimId}`} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                          <Presentation className="w-4 h-4 mr-2" />Executive View
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={exportToPDF} disabled={exportingPdf} data-testid="export-pdf-item">
                        {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                        PDF Report
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {!isPeopleLab && (
                    <>
                      <DropdownMenuItem onClick={exportToPDF} disabled={exportingPdf} data-testid="export-pdf-item">
                        {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                        PDF Report
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Data</DropdownMenuLabel>
                  <DropdownMenuItem onClick={exportToExcel} data-testid="export-summary-item">
                    <Download className="w-4 h-4 mr-2" />Summary (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Detailed Export</DropdownMenuLabel>
                  <DropdownMenuCheckboxItem checked={exportIncludeReasoning} onCheckedChange={setExportIncludeReasoning} data-testid="export-include-reasoning">Include AI Reasoning</DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem checked={exportIncludeConfidence} onCheckedChange={setExportIncludeConfidence} data-testid="export-include-confidence">Include Confidence Scores</DropdownMenuCheckboxItem>
                  <DropdownMenuItem onClick={() => exportDetailedResults('xlsx')} data-testid="export-xlsx-btn"><Download className="w-4 h-4 mr-2" />Export All as Excel (.xlsx)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportDetailedResults('csv')} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" />Export All as CSV</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {simulation && (
        <>
          {/* Report Header — interpretation-first context strip */}
          <ReportHeader simulation={simulation} population={population} stimulus={stimulus} />

          {/* Open-ended vs Closed-ended */}
          {isOpenEnded ? (
            <div className="mt-5"><OpenEndedAnalysis simulation={simulation} stimulus={stimulus} population={population} /></div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5 mt-5">
              <div ref={reportRef}>
                {(() => {
                  // Tabs: primary core + module + Reliability standalone (far right)
                  // Other advanced views (Social Dynamics, Exposure Analysis) remain in a small "More views" dropdown when present.
                  const overflowTabs = [];
                  if (simulation?.social_enabled && socialDynamics?.social_enabled) {
                    overflowTabs.push({ value: 'social-dynamics', label: 'Social Dynamics', Icon: Network });
                  }
                  if (exposureAnalysis?.has_exposure_analysis) {
                    overflowTabs.push({ value: 'exposure', label: 'Exposure Analysis', Icon: Globe });
                  }
                  const hasReliability = !!simulation?.pre_mode_used;
                  const activeOverflow = overflowTabs.find(t => t.value === activeTab);
                  return (
                    <div className="flex items-center gap-2 flex-wrap overflow-x-auto -mx-1 px-1" data-testid="analysis-tabs-row">
                      <TabsList className="bg-slate-100/80" data-testid="analysis-primary-tabs">
                        <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                        <TabsTrigger value="breakdown" data-testid="tab-breakdown">Demographic Breakdown</TabsTrigger>
                        <TabsTrigger value="data-visuals" data-testid="tab-data-visuals" className="flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" />Data Visuals</TabsTrigger>
                        <TabsTrigger value="insights" data-testid="tab-insights">AI Insights</TabsTrigger>
                      </TabsList>
                      {(isPeopleLab || isMarketLab || isPharmaLab) && (
                        <>
                          <span className="h-6 w-px bg-slate-200 mx-1" data-testid="analysis-tabs-module-divider" aria-hidden />
                          <TabsList className="bg-blue-50/80 border border-blue-100" data-testid="analysis-module-tabs">
                            {isPeopleLab && <TabsTrigger value="peoplelab" data-testid="tab-peoplelab" className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />PeopleLab</TabsTrigger>}
                            {isMarketLab && <TabsTrigger value="marketlab" data-testid="tab-marketlab" className="flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" />MarketLab</TabsTrigger>}
                            {isPharmaLab && <TabsTrigger value="pharmalab" data-testid="tab-pharmalab" className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" />PharmaLab</TabsTrigger>}
                          </TabsList>
                        </>
                      )}
                      {overflowTabs.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant={activeOverflow ? 'default' : 'outline'}
                              size="sm"
                              className="h-9 gap-1.5"
                              data-testid="tab-more-views-trigger"
                            >
                              {activeOverflow ? (
                                <>
                                  {activeOverflow.Icon && <activeOverflow.Icon className="w-3.5 h-3.5" />}
                                  {activeOverflow.label}
                                </>
                              ) : (
                                <>More views</>
                              )}
                              <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52" data-testid="tab-more-views-menu">
                            {overflowTabs.map(t => (
                              <DropdownMenuItem
                                key={t.value}
                                onSelect={() => setActiveTab(t.value)}
                                data-testid={`tab-more-views-${t.value}`}
                                className="gap-2"
                              >
                                {t.Icon && <t.Icon className="w-3.5 h-3.5" />}
                                {t.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {/* Reliability — standalone on far right, visually separated */}
                      {hasReliability && (
                        <div className="ml-auto flex items-center gap-2 pl-2 border-l border-slate-200" data-testid="reliability-tab-wrapper">
                          <TabsList className="bg-transparent p-0 h-auto" data-testid="analysis-reliability-tab">
                            <TabsTrigger
                              value="pre-analytics"
                              data-testid="tab-reliability"
                              className="h-9 px-3 gap-1.5 border border-slate-200 bg-white data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:border-slate-900"
                            >
                              <Shield className="w-3.5 h-3.5" />
                              Reliability
                            </TabsTrigger>
                          </TabsList>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-5 tab-content-enter">
                  <OverviewTab
                    aggregation={aggregation}
                    simulation={simulation}
                    orderedResponseScale={orderedResponseScale}
                    scaleColors={scaleColors}
                    scaleColorMap={scaleColorMap}
                    pieData={pieData}
                    webContextPack={webContextPack}
                    allBreakdowns={allBreakdowns}
                    population={population}
                    copiedData={copiedData}
                    copyChartData={copyChartData}
                    exportToCSV={exportToCSV}
                    setFullscreenChart={setFullscreenChart}
                  />
                </TabsContent>

                {/* Breakdown Tab */}
                <TabsContent value="breakdown" className="space-y-6 tab-content-enter">
                  <BreakdownTab
                    barData={barData}
                    breakdown={breakdown}
                    orderedResponseScale={orderedResponseScale}
                    scaleColors={scaleColors}
                    scaleColorMap={scaleColorMap}
                    groupBy={groupBy}
                    setGroupBy={setGroupBy}
                    population={population}
                    filterField={filterField}
                    setFilterField={setFilterField}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    allBreakdowns={allBreakdowns}
                    aggregation={aggregation}
                    crossDemographic1={crossDemographic1}
                    setCrossDemographic1={setCrossDemographic1}
                    crossDemographic2={crossDemographic2}
                    setCrossDemographic2={setCrossDemographic2}
                    crossAnalysisData={crossAnalysisData}
                    setCrossAnalysisData={setCrossAnalysisData}
                    crossAnalysisLoading={crossAnalysisLoading}
                    availableDemographics={availableDemographics}
                    stimulus={stimulus}
                  />
                </TabsContent>

                {/* AI Insights Tab */}
                <TabsContent value="insights" className="space-y-6">
                  <AIInsightsTab selectedSimId={selectedSimId} />
                </TabsContent>

                {/* PRE Analytics Tab */}
                <TabsContent value="pre-analytics" className="space-y-6">
                  <PREAnalytics
                    simulationId={selectedSimId}
                    simulation={simulation}
                    aggregation={aggregation}
                    orderedResponseScale={orderedResponseScale}
                    population={population}
                    stimulus={stimulus}
                  />
                </TabsContent>

                {/* Data Visuals Tab */}
                <TabsContent value="data-visuals" className="space-y-6 tab-content-enter">
                  <DataVisualsTab
                    selectedSimId={selectedSimId}
                    allBreakdowns={allBreakdowns}
                    aggregation={aggregation}
                    orderedResponseScale={orderedResponseScale}
                    scaleColors={scaleColors}
                    scaleColorMap={scaleColorMap}
                    onSwitchTab={(target) => {
                      if (target === 'ai-insights') setActiveTab('insights');
                      else if (target === 'demographic-breakdown') setActiveTab('breakdown');
                      else if (target === 'interview') setInterviewOpen(true);
                      else if (target === 'scenario') setScenarioOpen(true);
                      else if (target === 'export') exportToPDF();
                    }}
                  />
                </TabsContent>
                
                {/* PeopleLab Tab */}
                {isPeopleLab && (
                  <TabsContent value="peoplelab" className="space-y-5 tab-content-enter">
                    <HRLabReport simulation={simulation} simulationId={simulation?.id} />
                  </TabsContent>
                )}
                
                {/* MarketLab Tabs */}
                {isMarketLab && (
                  <TabsContent value="marketlab" className="space-y-5 tab-content-enter">
                    <MarketLabViews simulation={simulation} aggregation={aggregation} population={population} stimulus={stimulus} breakdown={breakdown} groupBy={groupBy} onGroupByChange={setGroupBy} />
                  </TabsContent>
                )}

                {/* PharmaLab Tab */}
                {isPharmaLab && (
                  <TabsContent value="pharmalab" className="space-y-5 tab-content-enter">
                    <PharmaLabReport simulation={simulation} />
                  </TabsContent>
                )}

                {/* Social Dynamics Tab */}
                {simulation?.social_enabled && socialDynamics?.social_enabled && (
                  <TabsContent value="social-dynamics" className="space-y-6 tab-content-enter">
                    <SocialDynamicsPanel data={socialDynamics} />
                    {socialDynamics?.cascades?.length > 0 && <CascadeAlerts cascades={socialDynamics.cascades} />}
                    <BehavioralClusterNetwork simulationId={selectedSimId} />
                    {socialDialogues?.has_dialogues && <MicroDialogues data={socialDialogues} />}
                  </TabsContent>
                )}

                {/* Exposure Analysis Tab */}
                {exposureAnalysis?.has_exposure_analysis && (
                  <TabsContent value="exposure" className="space-y-6 tab-content-enter">
                    <ExposureAnalysisPanel data={exposureAnalysis} />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          )}
        </>
      )}

      {/* Fullscreen Chart Modal */}
      <Dialog open={!!fullscreenChart} onOpenChange={() => setFullscreenChart(null)}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>{fullscreenChart === 'distribution' ? 'Response Distribution' : 'Demographic Breakdown'}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[400px]">
            {fullscreenChart === 'distribution' && pieData.length > 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius="70%" innerRadius="35%" dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`} labelLine animationDuration={800}>
                    {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={scaleColors[index % scaleColors.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} responses`, name]} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
